import i18next from 'i18next';
import copy from 'copy-to-clipboard';
import {
  all,
  takeEvery,
  call,
  put,
  select,
  race,
  take,
} from 'redux-saga/effects';
import {
  PapiRequestDaSharedGallectionFoldersCreate,
  PapiRequestDaSharedGallectionFoldersUpdate,
  PapiDaSharedGallectionFoldersCreate,
  PapiDaSharedGallectionFoldersUpdate,
  PapiDaSharedGallectionFoldersMove,
  PapiGallection,
  PapiDaSharedGallectionFoldersDelete,
  PapiRequestDaSharedGallectionFoldersDelete,
  PapiDaSharedGallectionFoldersUpdateOrder,
  PapiRequestDaSharedGallectionFoldersUpdateOrder,
  PapiRequestDaSharedGallectionFetch,
  PapiDaSharedGallectionFetch,
  PapiStudioPagesStash,
  PapiStudioBreadcrumbs,
  PapiRequestStudioPagesStash,
  PapiDaSharedGallectionFoldersMoveDeviations,
  PapiRequestDaSharedGallectionFoldersMoveDeviations,
} from '@wix/da-papi-types';
import { requestPuppy, isApiError, requestNapi } from '@wix/da-http-client';
import { BiData, StudioBatchUpdateBiEvent } from '@wix/da-bi/pkg/events';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import {
  pushGlobalConfirmationModal,
  confirmYes,
  confirmNo,
  pushModal,
} from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import {
  getItems,
  getStreamParam,
} from '@wix/da-shared-react/pkg/Stream/base/selectors';
import {
  addNotification,
  addErrorNotification,
} from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationDuration } from '@wix/da-shared-react/pkg/Notifications/Notification';
import { NotificationType } from '@wix/da-shared-react/pkg/Notifications';
import { Url } from '@wix/da-url';
import LinkIcon from '@wix/da-ds/pkg/Icons/24x24/Link';
import {
  addBookmark,
  removeBookmark,
} from '@wix/da-submit-shared/pkg/redux/actions';
import {
  confirmDeleteFolder,
  createFolder,
  deleteFolder,
  fetchFolder,
  moveDeviationsToFolder,
  moveSelectionToFolder,
  moveFolderUp,
  reloadSelectedFolder,
  updateFolder,
  updateFolderOrder,
  updateSelectedFolder,
  confirmMoveSelectionToFolder,
  createFolderFromSelection,
  setIsBusy,
  copyFolderLink,
  reloadSubfolders,
} from '../../actions/studio/folders';
import {
  getIsRootStashFolder,
  getSelectedDeviationIds,
  getSelectedFolder,
  getActiveSection,
} from '../../selectors/studio';
import { fetchFoldersStream } from '../stash';
import { refreshTable } from '../../actions/studio';
import { StudioSection } from '../../types/store';
import { FolderType } from '../../types/folders';
import {
  GALLERY_SUBFOLDERS_STREAM,
  STASH_SUBFOLDERS_STREAM,
} from '../../../constants';

function* handleCreateFolder({
  payload: { data, onSaveSuccess },
}: ReturnType<typeof createFolder>) {
  yield put(setIsBusy(true));

  const isStashRoot = yield select(getIsRootStashFolder);
  const selectedFolder: PapiGallection = yield select(getSelectedFolder);
  const parent_folder_id = isStashRoot
    ? undefined
    : selectedFolder.folderId < 1
      ? undefined
      : selectedFolder.folderId;

  const response: PapiDaSharedGallectionFoldersCreate | false = yield call(
    requestNapi,
    {
      method: 'post',
      url: 'gallection/folders/create',
      data: {
        ...data,
        parent_folder_id,
        editorRaw: JSON.stringify(data.description),
      } as PapiRequestDaSharedGallectionFoldersCreate,
    },
    undefined
  );

  if (!response || isApiError(response) || !response.gallection) {
    yield put(
      addErrorNotification(
        i18next.t('studio.modals.folder.errors.create'),
        NotificationDuration.S
      )
    );
    yield put(setIsBusy(false));
    return;
  }

  yield call(handleBookmarks, response.gallection, data.addToShortcuts);
  yield put(reloadSubfolders(data.type!));
  yield call(onSaveSuccess, response.gallection.folderId);
  yield put(setIsBusy(false));
}

function* handleBookmarks(folder: PapiGallection, add: boolean) {
  if (add && !folder.isBookmarked) {
    yield put(addBookmark(folder));
  } else if (!add && folder.isBookmarked) {
    yield put(removeBookmark(folder));
  }
}

function* handleUpdateFolder({
  payload: { folderId, data, doReload = true, onSaveSuccess, onError },
}: ReturnType<typeof updateFolder>) {
  yield put(setIsBusy(true));

  const response: PapiDaSharedGallectionFoldersUpdate | false = yield call(
    requestNapi,
    {
      method: 'post',
      url: 'gallection/folders/update',
      data: {
        folderid: folderId,
        ...data,
        editorRaw: JSON.stringify(data.description),
      } as PapiRequestDaSharedGallectionFoldersUpdate,
    },
    undefined
  );

  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(
        i18next.t('studio.modals.folder.errors.update'),
        NotificationDuration.S
      )
    );
    yield put(setIsBusy(false));
    if (onError) {
      yield call(onError);
    }
    return;
  }

  // TODO: Move this shortcut thing to a separate saga, please
  if (typeof data.addToShortcuts !== 'undefined') {
    const gallection = yield call(fetchGallection, folderId, data.type);
    if (gallection) {
      yield call(handleBookmarks, gallection, data.addToShortcuts);
    }
  }

  const selectedFolder: PapiGallection = yield select(getSelectedFolder);
  if (doReload && selectedFolder.folderId === folderId) {
    yield put(reloadSelectedFolder());
  } else if (doReload) {
    yield put(reloadSubfolders(selectedFolder.type));
  }

  yield call(onSaveSuccess, folderId);
  yield put(setIsBusy(false));
}

function* fetchGallection(folderId, type = 'stash') {
  const response: PapiDaSharedGallectionFetch = yield call(
    requestPuppy,
    {
      method: 'get',
      url: 'gallection/fetch',
      params: {
        type,
        folderid: folderId,
      } as PapiRequestDaSharedGallectionFetch,
    },
    undefined
  );

  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(
        i18next.t('common.somethingWentWrong'),
        NotificationDuration.S
      )
    );
    return;
  }

  return response.gallection;
}

function* handleFetchFolder({
  payload: { folderId, folderType, onFetchSuccess },
}: ReturnType<typeof fetchFolder>) {
  const gallection = yield call(fetchGallection, folderId, folderType);
  if (gallection) {
    yield call(onFetchSuccess, gallection);
  }
}

function* handleDeleteFolder({
  payload: { folder, onDeleteSuccess },
}: ReturnType<typeof deleteFolder>) {
  const response: PapiDaSharedGallectionFoldersDelete | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'gallection/folders/delete',
      data: {
        type: folder.type,
        folderid: folder.folderId,
      } as PapiRequestDaSharedGallectionFoldersDelete,
    },
    undefined
  );

  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(
        i18next.t('studio.modals.folder.errors.delete'),
        NotificationDuration.S
      )
    );
    return;
  }

  yield call(onDeleteSuccess);
}

function* handleConfirmDeleteFolder({
  payload: { folder, onDeleteSuccess },
}: ReturnType<typeof confirmDeleteFolder>) {
  if (folder.type === 'stash') {
    yield put(
      pushGlobalConfirmationModal({
        titleTransKey: 'studio.modals.deleteFolder.title',
        textTransKey: 'studio.modals.deleteFolder.text',
        confirmBtnType: 'danger',
        confirmBtnLabelTransKey: 'common.delete',
        withCancelButton: true,
      })
    );
  } else if (folder.type === 'gallery') {
    yield put(
      pushGlobalConfirmationModal({
        titleTransKey: 'studio.modals.deleteGallery.title',
        textTransKey: 'studio.modals.deleteGallery.text',
        confirmBtnType: 'danger',
        confirmBtnLabelTransKey: 'common.delete',
        withCancelButton: true,
      })
    );
  }

  const { no } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });

  if (no) {
    return;
  }

  yield put(deleteFolder(folder, onDeleteSuccess));
}

function* handleUpdateFolderOrder({
  payload: { folder, position },
}: ReturnType<typeof updateFolderOrder>) {
  yield put(setIsBusy(true));

  const response: PapiDaSharedGallectionFoldersUpdateOrder | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'gallection/folders/update_order',
      data: {
        type: folder.type,
        folderid: folder.folderId,
        position,
      } as PapiRequestDaSharedGallectionFoldersUpdateOrder,
    },
    undefined
  );

  yield put(setIsBusy(false));

  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(
        i18next.t('studio.modals.folder.errors.updateOrder'),
        NotificationDuration.S
      )
    );
    return;
  }

  // it'll probably be better to reload the subfolders one level up but for now this'll do
  yield put(reloadSubfolders(folder.type));
}

function* handleMoveFolderUp({
  payload: { folder },
}: ReturnType<typeof moveFolderUp>) {
  const breadcrumbs: PapiStudioBreadcrumbs = yield call(
    requestPuppy,
    {
      method: 'get',
      url: 'studio/breadcrumbs',
      params: { folderid: folder.folderId },
    },
    undefined,
    'v1'
  );
  if (
    !breadcrumbs ||
    isApiError(breadcrumbs) ||
    !breadcrumbs.breadcrumbs ||
    breadcrumbs.breadcrumbs.length < 2
  ) {
    yield put(
      addErrorNotification(
        i18next.t('common.somethingWentWrong'),
        NotificationDuration.S
      )
    );
    return;
  }

  const grandparent = breadcrumbs.breadcrumbs.at(-2);
  if (grandparent) {
    const response: PapiDaSharedGallectionFoldersMove = yield call(
      requestPuppy,
      {
        method: 'post',
        url: 'gallection/folders/move',
        data: {
          type: folder.type,
          folderid: folder.folderId,
          to_folderid: grandparent.folderId,
        },
      },
      undefined
    );
    if (!response || isApiError(response)) {
      yield put(
        addErrorNotification(
          i18next.t('common.somethingWentWrong'),
          NotificationDuration.S
        )
      );
      return;
    }

    yield put(reloadSubfolders(folder.type));
  }
}

function* handleMoveDeviationsToFolder({
  payload: { deviationIds, targetFolderId },
}: ReturnType<typeof moveDeviationsToFolder>) {
  // If we ever need to move from a different src folder we could add it as part of the action payload
  const selectedFolder: PapiGallection = yield select(getSelectedFolder);
  const response: PapiDaSharedGallectionFoldersMoveDeviations = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'gallection/folders/move_deviations',
      data: {
        type: selectedFolder.type,
        deviationids: deviationIds,
        src_folderid: selectedFolder.folderId,
        target_folderid: targetFolderId,
      } as PapiRequestDaSharedGallectionFoldersMoveDeviations,
    },
    undefined
  );
  if (!response || isApiError(response)) {
    yield put(
      addErrorNotification(
        i18next.t('common.somethingWentWrong'),
        NotificationDuration.S
      )
    );
    return;
  }

  yield put(refreshTable({ preservePage: true }));
}

function* handleMoveSelectionToFolder({
  payload: { targetFolderId },
}: ReturnType<typeof moveSelectionToFolder>) {
  const deviationIds: number[] = yield select(getSelectedDeviationIds);
  yield call(
    logBiEvent,
    BiData<StudioBatchUpdateBiEvent>({
      evid: 12,
      items_count: deviationIds.length,
      update_type: 'move_to_folder',
    })
  );
  yield put(moveDeviationsToFolder(deviationIds, targetFolderId));
}

function* handleConfirmMoveSelectionToFolder({
  payload: { targetFolderId },
}: ReturnType<typeof confirmMoveSelectionToFolder>) {
  yield put(
    pushGlobalConfirmationModal({
      titleTransKey: 'studio.modals.moveToFolder.title',
      textTransKey: 'studio.modals.moveToFolder.text',
      confirmBtnLabelTransKey: 'studio.modals.moveToFolder.confirmBtn',
      withCancelButton: true,
    })
  );

  const { no } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });

  if (no) {
    return;
  }

  yield put(moveSelectionToFolder(targetFolderId));
}

function* handleCreateFolderFromSelection() {
  const selectedFolder: PapiGallection = yield select(getSelectedFolder);
  const isStashRoot = yield select(getIsRootStashFolder);
  const selectedDeviationIds = yield select(getSelectedDeviationIds);
  yield put(
    pushModal(ModalType.STUDIO_FOLDER, {
      params: {
        folderType: selectedFolder.type,
        onSaveSuccess: function* (folderId: number) {
          yield call(
            logBiEvent,
            BiData<StudioBatchUpdateBiEvent>({
              evid: 12,
              items_count: selectedDeviationIds.length,
              update_type: isStashRoot ? 'create_folder' : 'create_subfolder',
            })
          );
          yield put(moveSelectionToFolder(folderId));
        },
      },
    })
  );
}

function* reloadSelectedStashFolder() {
  const selectedFolder: PapiGallection = yield select(getSelectedFolder);
  const response: PapiStudioPagesStash | false = yield call(
    requestPuppy,
    {
      method: 'get',
      url: 'studio/pages/stash',
      params: {
        init: false,
        folderid: selectedFolder.folderId,
      } as PapiRequestStudioPagesStash,
    },
    undefined,
    'v1'
  );
  if (!response || isApiError(response) || !response.rootFolder) {
    yield put(
      addErrorNotification(
        i18next.t('common.somethingWentWrong'),
        NotificationDuration.S
      )
    );
    return;
  }
  yield put(updateSelectedFolder(response.rootFolder));
}

function* reloadSelectedGalleryFolder() {
  const selectedFolder: PapiGallection = yield select(getSelectedFolder);
  const response: PapiStudioPagesStash | false = yield call(
    requestPuppy,
    {
      method: 'get',
      url: 'studio/pages/published',
      params: {
        init: false,
        folderid: selectedFolder.folderId,
      } as PapiRequestStudioPagesStash,
    },
    undefined,
    'v1'
  );
  if (!response || isApiError(response) || !response.rootFolder) {
    yield put(
      addErrorNotification(
        i18next.t('common.somethingWentWrong'),
        NotificationDuration.S
      )
    );
    return;
  }
  yield put(updateSelectedFolder(response.rootFolder));
}

function* handleReloadSelectedFolder() {
  const activeSection = yield select(getActiveSection);
  if (activeSection === StudioSection.STASH) {
    yield call(reloadSelectedStashFolder);
  } else if (activeSection === StudioSection.PUBLISHED) {
    yield call(reloadSelectedGalleryFolder);
  }
}

function* handleCopyFolderLink({ payload: { folder } }) {
  const currentUser = yield select(getCurrentUser);
  if (folder.type === 'gallery') {
    yield call(
      copy,
      Url.userGalleryLink({
        username: currentUser.username,
        folderId: folder.folderId,
        folderName: folder.name,
        gallectionType: folder.type,
      })
    );
  }
  if (folder.type === 'stash') {
    yield call(copy, Url.studioStash(folder.folderId));
  }
  yield put(
    addNotification(NotificationType.Generic, {
      messageLocaleKey: 'studio.notifications.copyLink.text',
      cardProps: {
        iconComponent: LinkIcon,
      },
    })
  );
}

function getStreamIdByFolderType(type?: 'stash' | 'gallery') {
  if (type === 'gallery') {
    return GALLERY_SUBFOLDERS_STREAM;
  }
  return STASH_SUBFOLDERS_STREAM;
}

function* handleReloadSubfolders({ payload: { type } }) {
  const {
    streamId,
    streamConfig: { itemsPerFetch },
  } = getStreamIdByFolderType(type);
  const { totalItemsCount, folderId, username } = yield select(state => ({
    totalItemsCount: getItems(state, streamId).length || 0,
    folderId: getStreamParam(state, streamId, 'folderId'),
    username: getStreamParam(state, streamId, 'username'),
  }));
  yield call(fetchFoldersStream, {
    type: type as FolderType,
    streamId,
    folderId: folderId < 0 ? undefined : folderId,
    username,
    offset: 0,
    limit: Math.max(totalItemsCount, itemsPerFetch),
    reset: true,
  });
}

export default function* foldersSaga() {
  yield all([
    takeEvery(createFolder, handleCreateFolder),
    takeEvery(updateFolder, handleUpdateFolder),
    takeEvery(fetchFolder, handleFetchFolder),
    takeEvery(deleteFolder, handleDeleteFolder),
    takeEvery(confirmDeleteFolder, handleConfirmDeleteFolder),
    takeEvery(updateFolderOrder, handleUpdateFolderOrder),
    takeEvery(moveFolderUp, handleMoveFolderUp),
    takeEvery(moveDeviationsToFolder, handleMoveDeviationsToFolder),
    takeEvery(moveSelectionToFolder, handleMoveSelectionToFolder),
    takeEvery(confirmMoveSelectionToFolder, handleConfirmMoveSelectionToFolder),
    takeEvery(createFolderFromSelection, handleCreateFolderFromSelection),
    takeEvery(reloadSelectedFolder, handleReloadSelectedFolder),
    takeEvery(copyFolderLink, handleCopyFolderLink),
    takeEvery(reloadSubfolders, handleReloadSubfolders),
  ]);
}
